// One audio owner for the whole page. A voice demo takes priority until closed.
export function createAudioCoordinator() {
  const media = new Map();
  const listeners = new Set();
  let voiceOwner = null;
  let audioOwner = null;

  const silence = (entry, notify = true) => {
    entry.element.muted = true;
    entry.element.volume = 0;
    entry.element.pause();
    if (notify) entry.onMuted();
  };
  const emitVoice = () => {
    listeners.forEach(listener => listener());
    window.dispatchEvent(new CustomEvent('tara-widget-active', { detail: { active: voiceOwner !== null } }));
  };

  return {
    getVoice: () => voiceOwner,
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    register(id, element, onMuted) {
      const entry = { element, onMuted };
      media.set(id, entry);
      element.defaultMuted = true;
      element.muted = true;
      element.volume = 0;
      const guard = () => {
        if (voiceOwner !== null || (!element.muted && audioOwner !== id)) silence(entry);
      };
      element.addEventListener('play', guard);
      element.addEventListener('volumechange', guard);
      if (voiceOwner !== null) silence(entry);
      return () => {
        element.removeEventListener('play', guard);
        element.removeEventListener('volumechange', guard);
        silence(entry, false);
        if (media.get(id) === entry) media.delete(id);
        if (audioOwner === id) audioOwner = null;
      };
    },
    requestAudio(id) {
      if (voiceOwner !== null || !media.has(id)) return false;
      for (const [otherId, entry] of media) if (otherId !== id) silence(entry);
      audioOwner = id;
      const element = media.get(id).element;
      element.muted = false;
      element.volume = 1;
      element.play().catch(() => {});
      return true;
    },
    mute(id) {
      if (media.has(id)) silence(media.get(id));
      if (audioOwner === id) audioOwner = null;
    },
    openVoice(id) {
      voiceOwner = id;
      audioOwner = null;
      media.forEach(entry => silence(entry));
      emitVoice();
    },
    closeVoice(id) {
      if (voiceOwner !== id) return;
      voiceOwner = null;
      // Do not resume or unmute previous media after a conversation.
      emitVoice();
    },
  };
}

export const audioCoordinator = createAudioCoordinator();
